// Regions of the world and the flat local projection used inside each one.
// bbox is [south, west, north, east] in degrees. x runs east and z runs south,
// in metres from the region's origin. hub: the town a coach to the region
// goes to. spawn: where a newcomer first stands. quietMonday: whether Mondays
// are kept as a stay-at-home day there. nightBikeBan: whether commercial bikes
// are kept off the road at night. coachFare: the fare to or from the region.
// park: part of the name of a place that is a motor park although the map
// does not mark it as one.
export const REGIONS = {
  northwest: {
    name: 'North West',
    lat0: 5.9597,
    lon0: 10.146,
    bbox: [5.75, 9.82, 6.12, 10.3],
    timezone: 'Africa/Douala',
    hub: 'Bamenda',
    spawn: { x: 619, z: 30, yaw: Math.PI }, // Commercial Avenue, a little north of City Chemist
    quietMonday: true,
    nightBikeBan: true,
    coachFare: 6500,
  },
  southwest: {
    name: 'South West',
    lat0: 4.1527,
    lon0: 9.2415,
    bbox: [3.98, 9.15, 4.2, 9.36],
    timezone: 'Africa/Douala',
    hub: 'Buea',
    park: 'mile 17',
    spawn: { x: 0, z: 0, yaw: 0 },
    quietMonday: false,
    nightBikeBan: false,
    coachFare: 6500,
  },
};

// The region whose map holds a real position, or null when the position is outside both.
export function regionAt(lat, lon) {
  for (const [id, r] of Object.entries(REGIONS)) {
    const [s, w, n, e] = r.bbox;
    if (lat >= s && lat <= n && lon >= w && lon <= e) return id;
  }
  return null;
}

// The motor parks of a region, where coaches are boarded and set down:
// the bus stations the map marks, and the place the region names as one.
export function motorParks(region, places) {
  const parks = places.filter((p) => p.k === 'bus' || (region.park && p.n.toLowerCase().includes(region.park)));
  return parks.map((p) => ({ n: p.n, x: p.x, z: p.z }));
}

// How far a road surface stands above the heightmap (roads.js lifts tar about 0.14 m and
// laterite about 0.08 m), so feet and wheels stand on the tar rather than in it. surface: 0 tar,
// 1 earth, -1 none, as Chunks.surfaceAt gives it.
export function surfaceLift(surface) {
  return surface === 0 ? 0.14 : surface === 1 ? 0.08 : 0;
}

export const TILE = 512; // metres per map tile
export const CELL = 128; // metres per streaming cell on the client (the tile files stay 512 m)
const M_PER_DEG_LAT = 110574;
const M_PER_DEG_LON = 111320;

export function project(region, lat, lon) {
  const k = Math.cos((region.lat0 * Math.PI) / 180) * M_PER_DEG_LON;
  return [(lon - region.lon0) * k, -(lat - region.lat0) * M_PER_DEG_LAT];
}

export function unproject(region, x, z) {
  const k = Math.cos((region.lat0 * Math.PI) / 180) * M_PER_DEG_LON;
  return [region.lat0 - z / M_PER_DEG_LAT, region.lon0 + x / k];
}

// The region's extent in local metres: { x0, z0, x1, z1 } with x0 < x1 and z0 < z1.
export function regionBounds(region) {
  const [s, w, n, e] = region.bbox;
  const [x0, z1] = project(region, s, w);
  const [x1, z0] = project(region, n, e);
  return { x0, z0, x1, z1 };
}

export function tileOf(v) {
  return Math.floor(v / TILE);
}

export function tileKey(tx, tz) {
  return `${tx}_${tz}`;
}
